// src/components/JobDetailsModal.js
import React, { useMemo, useCallback, useState, useEffect } from 'react'; // <-- Import useState and useEffect
import { useNavigate } from 'react-router-dom';
import { useJobs } from '../context/jobContext';
import { useData } from '../context/DataContext';
import styles from './JobDetailsModal.module.css';
import SortableTable from '../utils/SortableTable';
import Spinner from './Spinner';

const JobDetailsModal = ({ isOpen, onClose, jobName }) => {
  console.log(
    `[JobDetailsModal Render] Received isOpen prop with value: ${isOpen}`,
  );
  const { setQueryResults, queryResults, addTask } = useData();

  // --- NEW: State to manage CSS animation class ---
  const [isMounted, setIsMounted] = useState(false);

  // --- NEW: Effect to apply the .show class after the component is in the DOM ---
  useEffect(() => {
    if (isOpen) {
      // Use a tiny timeout to allow the DOM to update before adding the 'show' class, which triggers the animation.
      const timer = setTimeout(() => setIsMounted(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsMounted(false);
    }
  }, [isOpen]);

  const { selectedJobDetails, selectedJobType, setDiscoveryJobState } =
    useJobs();
  const navigate = useNavigate();
  const { setActiveinfluencerId } = useData(); // <-- Import setActiveinfluencerId from useData

  const handleRowClick = useCallback(
    (influencerRow) => {
      if (selectedJobType === 'DISPATCH_INVOICES') return;
      console.log('Navigating to influencer:', influencerRow.influencerId);
      setActiveinfluencerId(influencerRow.influencerId);
      console.log('resultData: ', influencerRow.resultData);
      // --- ROBUST DATA EXTRACTION & PARSING ---
      let jobResultData = influencerRow.resultData || influencerRow.resultdata;

      // 1Fix: Check if it's a string and parse it
      if (typeof jobResultData === 'string') {
        try {
          console.log('Parsing resultData JSON string:', jobResultData);
          jobResultData = JSON.parse(jobResultData);
        } catch (e) {
          console.error('Failed to parse resultData JSON:', e);
          // If parsing fails, we might want to pass it as a simple message or null
          // to avoid crashing the next page.
        }
      }

      const newChannel = jobResultData?.trace?.step_4_execution?.channel;

      if (newChannel && queryResults) {
        console.log(
          `[JobModal] Optimistically updating channel to ${newChannel} for influencer ${influencerRow.influencerId}`,
        );

        setQueryResults((prevResults) => {
          return prevResults.map((inf) => {
            if (inf.influencerId === influencerRow.influencerId) {
              return { ...inf, activeInfluencerChannel: newChannel };
            }
            return inf;
          });
        });
      }

      // 2. FIX: Optimistically Add To-Do to Global State
      const followUpStep = jobResultData?.trace?.step_5_followup;

      // We check if we have a valid ID and if the task was actually created
      if (followUpStep?.status === 'Success' && followUpStep?.todoId) {
        console.log(
          `[JobModal] Optimistically adding Task ${followUpStep.todoId} to global state`,
        );

        // Construct a task object that matches your DB schema / Frontend Model
        const newTask = {
          id: followUpStep.todoId,
          influencerId: influencerRow.influencerId,
          text: followUpStep.task, // The text generated by backend
          date: followUpStep.date, // YYYY-MM-DD
          completed: 0, // 0 for false in MySQL usually
          taskType: 'AUTOEMAIL',
          status: 'pending', // Visual status
          // Add placeholder values for fields not in the trace but required by UI
          nameMenu: '',
          prompt: '',
        };

        // Dispatch to Context
        addTask(newTask);
      }

      const navigationState = {
        agentJobResult: jobResultData,
        errorMessage: influencerRow.errorMessage,
        fromAgent: true,
        influencerName: influencerRow.name, // Pass Name
        influencerId: influencerRow.influencerId, // Pass ID
        activeChannelOverride: newChannel, // <--- PASS THE NEW CHANNEL
      };

      let destination = '/influencer';

      // SMART NAVIGATION SWITCH
      switch (selectedJobType) {
        case 'CHANNEL_ANALYSIS':
          destination = '/influencer';
          break;
        case 'DEEP_CONTENT_ANALYSIS':
          destination = '/publication';
          break;
        case 'RELATIONSHIP_SUMMARY':
          destination = '/messages';
          break;
        case 'KNOWLEDGE_SUMMARY':
          destination = '/knowledge';
          break;
        case 'SEND_MESSAGES':
          destination = '/email';
          break;
        case 'DISPATCH_INVOICES': // <--- THE FIX
          // Do absolutely nothing. Just return early.
          return;
        case 'DISCOVERY_FULL_CYCLE':
          if (influencerRow.taskStatus === 'failed') {
            destination = '/discovery'; // Route rejects to Discovery Page
            const rejectPhase = jobResultData?.rejectPhase;
            const platformId = jobResultData?.platformId;

            // --- INJECT AGENT DATA INTO DISCOVERY PAGE STATE ---
            // This hydrates the Discovery Page tables without needing a new backend endpoint
            const aiRejects = [];
            const onHolds = [];

            selectedJobDetails.forEach((row) => {
              let rData = row.resultData;
              if (typeof rData === 'string') {
                try {
                  rData = JSON.parse(rData);
                } catch (e) {
                  rData = {};
                }
              }

              if (row.taskStatus === 'failed') {
                const formattedRow = {
                  platformId: rData.platformId,
                  name: rData.name || row.name,
                  countryFullName: rData.country || row.country, // <--- FIXED KEY
                  subscriberCount: rData.subscriberCount, // <--- ADDED
                  viewCount: rData.viewCount, // <--- ADDED
                  thumbnailUrl: rData.thumbnailUrl, // <--- ADDED
                  channelUrl: rData.channelUrl, // <--- ADDED
                  healthTier: rData.healthTier,
                  healthScore: rData.healthAnalysis?.healthScore,
                  contentFocusTier: rData.contentFocusTier,
                  marketFitTier: rData.marketFitTier,
                  contentQualityTier: rData.contentQualityTier,
                  healthAnalysis: rData.healthAnalysis,
                  contentFocusAnalysis: rData.contentFocusAnalysis,
                  marketFitAnalysis: rData.marketFitAnalysis,
                  contentQualityAnalysis: rData.contentQualityAnalysis,
                  videos_for_import: rData.videos_for_import || [],
                  reasonCode:
                    rData.rejectPhase === 1
                      ? 'AI_REJECT_PHASE_1'
                      : 'AI_REJECT_PHASE_2',
                };

                if (rData.rejectPhase === 1 || rData.rejectPhase === 2) {
                  aiRejects.push(formattedRow);
                }
              }
            });

            // Hydrate the Discovery Page with the Agent's failures
            setDiscoveryJobState({
              jobStatus: 'finished',
              results: {
                newInfluencers: [],
                existingYouTubeInfluencers: [],
                similarInfluencers: [],
                discardedInfluencers: [],
                aiRejectedInfluencers: aiRejects,
                onHoldInfluencers: onHolds, // This will be empty, which is correct!
                warnings: [`Viewing AI Rejects from Agent Job: "${jobName}"`],
              },
              error: null,
            });

            if (rejectPhase) {
              // Send all AI Rejects to the AI Rejects tab
              navigationState.activeTab = 'ai_rejected';
              navigationState.highlightPlatformId = platformId;
            } else {
              navigationState.activeTab = 'discarded'; // Fallback
            }
          } else {
            // Acquired influencers go to the Query Page (CRM)
            destination = '/query';
            navigationState.fromAgentDiscovery = true;
          }
          break;
        default:
          destination = '/influencer';
      }
      navigate(destination, { state: navigationState });
      onClose();
    },
    [
      navigate,
      onClose,
      setActiveinfluencerId,
      selectedJobType,
      queryResults,
      setQueryResults,
      addTask,
    ],
  );

  const columns = useMemo(() => {
    const baseCols = [
      { header: 'Channel Name', accessorKey: 'name', size: 250 },
      { header: 'Status', accessorKey: 'status', size: 100 },
      { header: 'Country', accessorKey: 'country', size: 120 },
      {
        header: 'Outcome',
        accessorKey: 'taskStatus',
        size: 140,
        cell: ({ row }) => {
          const status = row.original.taskStatus;
          let badgeClass = styles.pending;
          let label = 'Processing...';

          // Custom Badge Logic for Autonomous Discovery
          if (selectedJobType === 'DISCOVERY_FULL_CYCLE') {
            if (status === 'success') {
              badgeClass = styles.success;
              label = 'Acquired';
            } else if (status === 'failed') {
              badgeClass = styles.failed;
              label = 'Excluded';
            }
          }
          // Standard Badge Logic for other jobs
          else {
            if (status === 'success') {
              badgeClass = styles.success;
              label = 'Success';
            } else if (status === 'failed') {
              badgeClass = styles.failed;
              label = 'Failed';
            }
          }

          return (
            <span className={`${styles.taskStatusBadge} ${badgeClass}`}>
              {label}
            </span>
          );
        },
      },
      {
        // Dynamic Column that combines Error Messages and JSON insights
        header: 'AI Insights & Details',
        size: 500,
        cell: ({ row }) => {
          const { errorMessage, resultData, taskStatus } = row.original;

          // 1. Determine Text Color based on Status
          let textColor = '#aaaaaa'; // Default: Grey (Pending)
          if (taskStatus === 'success') textColor = '#a0d468'; // Green (Acquired)
          if (taskStatus === 'failed') textColor = '#ff8a80'; // Red (Excluded)

          // 1. Parse the JSON resultData securely
          let data = resultData;
          if (typeof data === 'string') {
            try {
              data = JSON.parse(data);
            } catch (e) {
              data = {};
            }
          }

          // 2. Formatting for Autonomous Discovery
          if (selectedJobType === 'DISCOVERY_FULL_CYCLE') {
            const isError = taskStatus === 'failed';
            let displayMessage =
              data?.action || errorMessage || 'Analyzing content...';

            // --- UX ENHANCEMENT: Borderline Confidence Warning ---
            // If the Agent rejected it, but the Health or Focus was somewhat decent, flag it!
            if (isError && data?.rejectPhase === 1) {
              const hTier = data?.healthAnalysis?.healthTier;
              const fTier = data?.contentFocusAnalysis?.focusTier;
              if (
                ['Prime', 'Promising', 'Acceptable'].includes(hTier) ||
                ['Excellent', 'Great', 'Good'].includes(fTier)
              ) {
                displayMessage = `⚠️ [Review] ${displayMessage}`;
              }
            }

            return (
              <div
                style={{
                  fontSize: '0.9em',
                  fontWeight: isError ? 'bold' : 'normal',
                  color: textColor,
                  fontStyle: 'italic',
                  cursor: isError ? 'pointer' : 'default',
                  textDecoration: isError ? 'underline dotted' : 'none',
                }}
                title={
                  isError ? 'Click to view full analysis in Discovery' : ''
                }
              >
                {displayMessage}
              </div>
            );
          }

          // 3. Formatting for Dispatch Invoices
          if (selectedJobType === 'DISPATCH_INVOICES') {
            if (taskStatus === 'failed') {
              return (
                <div style={{ color: '#ff8a80', fontSize: '0.85em' }}>
                  {errorMessage || 'Unknown Error'}
                </div>
              );
            }
            return (
              <div
                style={{
                  fontSize: '0.85em',
                  color: textColor,
                  fontStyle: 'italic',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {data?.greeting || 'System Default Greeting'}
              </div>
            );
          }

          // 4. Default Fallback for all other Agent Jobs
          if (taskStatus === 'failed') {
            return (
              <div style={{ color: textColor, fontSize: '0.85em' }}>
                {errorMessage}
              </div>
            );
          }
          return (
            <div style={{ color: textColor, fontSize: '0.85em' }}>
              Task completed successfully.
            </div>
          );
        },
      },
    ];

    return baseCols;
  }, [selectedJobType]);

  if (!isOpen) {
    console.log('[JobDetailsModal Render] isOpen is false, rendering null.');
    return null;
  }

  return (
    // --- MODIFIED: Add conditional .show class ---
    <div
      className={`${styles.modalOverlay} ${isMounted ? styles.show : ''}`}
      onClick={onClose}
    >
      <div
        className={`${styles.modalContent} ${isMounted ? styles.show : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h3>
            Job Details
            {jobName && (
              <span
                style={{
                  fontWeight: 'normal',
                  color: '#bbb',
                  marginLeft: '0.5rem',
                }}
              >
                for "{jobName}"
              </span>
            )}
          </h3>
          <button onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          {!selectedJobDetails ? (
            <Spinner message="Loading job details..." />
          ) : (
            <SortableTable
              data={selectedJobDetails}
              columns={columns}
              onRowClick={handleRowClick}
              enableStickyHeader={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetailsModal;
